import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import RowerCard from './RowerCard';
import { filterRowers, isRowerAssigned } from '../../utils/rowerFilters';

const AvailableRowers = ({
  availableRowers,
  trainingGroups,
  selectedCrew,
  onDragStart,
  onDragEnd,
}) => {
  const [selectedTrainingGroup, setSelectedTrainingGroup] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Debug logging for training group data
  useEffect(() => {
    console.log('=== AVAILABLE ROWERS DEBUG ===');
    console.log('Training Groups:', trainingGroups);
    console.log('Sample rower data (first 3):');
    availableRowers.slice(0, 3).forEach((rower, index) => {
      console.log(`Rower ${index + 1}:`, {
        name: `${rower.first_name} ${rower.last_name}`,
        id: rower.id,
        training_groups: rower.training_groups,
        training_groups_count: rower.training_groups?.length || 0,
        raw_rower: rower,
      });
    });
  }, [availableRowers, trainingGroups]);

  const filteredRowers = filterRowers(availableRowers, searchTerm, selectedTrainingGroup);

  const handleDragStart = (e, rower) => {
    onDragStart(e, rower);
  };

  const handleDragEnd = (e) => {
    if (onDragEnd) {
      onDragEnd(e);
    }
  };

  return (
    <div className="flex h-full flex-col rounded-lg bg-white p-6 shadow-md">
      <div className="mb-6 border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-800">Available Rowers</h2>
        <p className="mt-1 text-sm text-gray-600">
          {selectedCrew
            ? `Drag rowers to ${selectedCrew.is_experimental ? 'add them to' : 'specific positions in'} ${selectedCrew.name}`
            : 'Select a crew to assign rowers'}
        </p>
      </div>

      <div className="mb-6 space-y-4">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search rowers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="training-group-filter">
          <select
            value={selectedTrainingGroup}
            onChange={(e) => setSelectedTrainingGroup(e.target.value)}
            className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-transparent focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Training Groups</option>
            {trainingGroups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto">
        {filteredRowers.length > 0 ? (
          filteredRowers.map((rower) => {
            const isAssigned = isRowerAssigned(rower, selectedCrew);
            return (
              <RowerCard
                key={rower.id}
                rower={rower}
                isAssigned={isAssigned}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                showStatus={true}
              />
            );
          })
        ) : (
          <div className="py-8 text-center text-gray-500">
            <p>No rowers found</p>
            {searchTerm && <p className="mt-2 text-sm">Try adjusting your search or filter</p>}
          </div>
        )}
      </div>

      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="text-sm text-gray-600">
          <span className="font-medium">Total:</span> {filteredRowers.length} rowers
          {selectedTrainingGroup !== 'all' && (
            <span className="ml-2 text-gray-400">(filtered by training group)</span>
          )}
        </div>
      </div>
    </div>
  );
};

AvailableRowers.propTypes = {
  availableRowers: PropTypes.array.isRequired,
  trainingGroups: PropTypes.array.isRequired,
  selectedCrew: PropTypes.object,
  onDragStart: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func,
};

export default AvailableRowers;
